import {
  Badge,
  Box,
  Button,
  HStack,
  MenuButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  BrandLogo,
  DataTable,
  Link,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  BrandOrderByColumnEnum,
  ProjectsWhereColumn,
  SqlOperator,
} from "@src/__generated__/urql-graphql";
import { LinkIconOnHover } from "@src/components/modules/clients/listing/LinkIconOnHover";
import { Icon } from "@src/components/ui-kit/Icon";
import { filtersToUrlParam } from "@src/utils/routing";
import { map } from "lodash";
import { observer } from "mobx-react-lite";
import { useContext, useMemo } from "react";
import { PopoverBrandActions } from "../../detail/PopoverBrandActions";
import type { BrandModel } from "../models";
import { PageBrandsCtx } from "../PageBrands";

type TData = {
  id: BrandModel["id"];
  name: BrandModel["name"];
  client: BrandModel["client"];
  archived_at: BrandModel["archived_at"];
  contacts: BrandModel["contacts"];
  stats: BrandModel["stats"];
  creditAccounts: BrandModel["creditAccounts"];
  code: BrandModel["code"];
  image: BrandModel["image"];
  defaultBillingEntityId: BrandModel["defaultBillingEntityId"];
  pipedriveCustomFieldValues: BrandModel["pipedriveCustomFieldValues"];
  actions: string;
  refetch?: () => void;
};

export const TableBrands = observer(function TableBrands() {
  const store = useContext(PageBrandsCtx);

  const data: TData[] = store.brands.map((i) => ({
    id: i.id,
    name: i.name,
    code: i.code,
    image: i.image,
    client: i.client,
    archived_at: i.archived_at || undefined,
    stats: i.stats,
    contacts: i.contacts,
    creditAccounts: i.creditAccounts,
    defaultBillingEntityId: i.defaultBillingEntityId,
    pipedriveCustomFieldValues: i.pipedriveCustomFieldValues,
    actions: "",
    refetch: store.refetch,
  }));

  const customFieldLabels = useMemo(() => {
    const labels: string[] = [];
    const labelsSet = new Set<string>();

    data.forEach((brand) => {
      brand.pipedriveCustomFieldValues.forEach((item) => {
        const label = item.customField.label?.trim();
        if (!label || labelsSet.has(label)) return;
        labelsSet.add(label);
        labels.push(label);
      });
    });

    return labels;
  }, [data]);

  const columns: BodyColumnDef<TData, BrandOrderByColumnEnum>[] =
    useMemo(() => {
      const cols: BodyColumnDef<TData, BrandOrderByColumnEnum>[] = [
        {
          id: "name",
          meta: { label: t`Brand` },
          header: () => t`Brand`,
          orderBy: BrandOrderByColumnEnum.BrandName,
          cell: ({ row }) => {
            const r = row.original;

            return (
              <HStack justifyContent="space-between">
                <HStack>
                  <BrandLogo mr="2" src={r.image?.urls.thumbnail} />
                  <LinkIconOnHover dataComponentName="view-brand-text">
                    <Link
                      href={{ pathname: "/brands/detail", query: { id: r.id } }}
                    >
                      <Text display="flex" flexFlow="row">
                        {r.name}
                      </Text>
                    </Link>
                    &nbsp;
                  </LinkIconOnHover>
                </HStack>
                {!!r.archived_at && (
                  <Badge ml="3" colorScheme="red">
                    <Trans>Archived</Trans>
                  </Badge>
                )}
              </HStack>
            );
          },
        },
        {
          id: "client",
          meta: { label: t`Client` },
          header: () => t`Client`,
          orderBy: BrandOrderByColumnEnum.ClientName,
          cell: ({ row }) => {
            const r = row.original;

            return (
              <div className="flex items-center">
                <Tooltip hasArrow label={t`View client page`} placement="top">
                  <Box>
                    <LinkIconOnHover dataComponentName="view-client-text">
                      <Link
                        href={{
                          pathname: "/clients/detail",
                          query: { id: r.client.id },
                        }}
                      >
                        <Text display="flex" flexFlow="row">
                          {r.client.name}
                        </Text>
                      </Link>
                      &nbsp;
                    </LinkIconOnHover>
                  </Box>
                </Tooltip>
              </div>
            );
          },
        },
        {
          id: "contacts",
          meta: { label: t`Contact person` },
          header: () => t`Contact person`,
          cell: ({ row }) => {
            const r = row.original;
            const CONTACTS_DISPLAYED_LIMIT = 2;
            const contactsLength = r.contacts.length;
            const contactsToPrint = r.contacts.slice(
              0,
              CONTACTS_DISPLAYED_LIMIT,
            );
            return (
              <div className="flex items-center">
                {map(
                  contactsToPrint,
                  (contact, index) =>
                    `${contact.first_name} ${contact.last_name}${
                      index < contactsToPrint.length - 1 ? ", " : ""
                    }`,
                )}
                {contactsLength > CONTACTS_DISPLAYED_LIMIT &&
                  `
            (
            ${t`and`} ${
              r.contacts.slice(CONTACTS_DISPLAYED_LIMIT, contactsLength).length
            } ${t`others`} )
          `}
              </div>
            );
          },
        },
      ];

      for (const label of customFieldLabels) {
        cols.push({
          id: `pipedrive-custom-field-${label}`,
          meta: { label },
          header: label,
          cell: ({ row }) =>
            row.original.pipedriveCustomFieldValues.find(
              (item) => item.customField.label === label,
            )?.value ?? "",
        });
      }

      cols.push(
        {
          id: "stats",
          meta: { label: t`Active projects` },
          header: () => t`Active projects`,
          orderBy: BrandOrderByColumnEnum.ActiveProjects,
          cell: ({ row }) => {
            const { stats, id } = row.original;

            return (
              <Link
                href={{
                  pathname: "/projects",
                  query: filtersToUrlParam([
                    {
                      column: ProjectsWhereColumn.BrandId,
                      value: [id],
                      operator: SqlOperator.In,
                    },
                  ]),
                }}
              >
                {stats.active_projects}
              </Link>
            );
          },
        },
        {
          id: ACTIONS_COLUMN_ID,
          meta: { label: t`Actions` },
          header: () => t`Actions`,
          cell: ({ row }) => {
            const brand = row.original;

            return (
              <PopoverBrandActions
                brand={{
                  ...brand,
                  archived_at: brand.archived_at
                    ? new Date(brand.archived_at)
                    : undefined,
                  defaultBillingEntityId:
                    brand.defaultBillingEntityId ?? undefined,
                }}
                client={brand.client}
                onAfterUpdate={() => {
                  brand.refetch?.();
                }}
                onAfterDelete={() => {
                  brand.refetch?.();
                }}
                trigger={
                  <MenuButton as={Button} colorScheme="grey" variant="ghost">
                    <Icon name="dots-horizontal" color="grey.700" />
                  </MenuButton>
                }
              />
            );
          },
        },
      );

      return cols;
    }, [customFieldLabels]);

  return (
    <DataTable.Body
      tableKey="brands"
      data={data}
      columns={columns}
      orderedBy={store.orderBy.clauses[0]}
      onOrderByChange={(clause) => {
        if (!clause.order) {
          store.orderBy.setClauses([]);
          return;
        }

        store.orderBy.setClauses([
          { column: clause.column, order: clause.order },
        ]);
      }}
      allIds={store.allBrandIds}
      enableRowSelection
    />
  );
});
